
import React, { useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import {
  MdMonitor, MdDeviceHub, MdTimeline, MdBugReport,
  MdStorage, MdSpeed, MdMemory, MdNetworkCheck,
  MdDns, MdList
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { HiOutlineServer, HiOutlineChip } from "react-icons/hi";
import { TbContainer } from "react-icons/tb";
import host1Img from "../../assets/host1.png";
import host2Img from "../../assets/host2.png";
import host3Img from "../../assets/host3.png";
import host4Img from "../../assets/host4.png";
import host5Img from "../../assets/host5.png";
import host6Img from "../../assets/host6.png";
import host7Img from "../../assets/host7.png";
import host8Img from "../../assets/host8.png";

/* ─── tiny shared primitives ─── */
const Breadcrumb = ({ items }) => (
  <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
    {items.map((item, i) => (
      <span key={i} className="flex items-center gap-2">
        {i > 0 && <FiChevronRight className="text-[14px]" />}
        {item.href
          ? <a className="hover:text-primary hover:underline" href={item.href}>{item.label}</a>
          : <span className="text-slate-900 dark:text-white">{item.label}</span>}
      </span>
    ))}
  </nav>
);

const SectionHeading = ({ icon, label, id, color = "primary/10 text-primary" }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={`w-8 h-8 rounded bg-${color} flex items-center justify-center`}>
      {icon}
    </div>
    <h2 id={id} className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white scroll-mt-24">
      {label}
    </h2>
  </div>
);

const StepItem = ({ num, children }) => (
  <li className="flex gap-3 items-start">
    <span className="mt-0.5 min-w-[1.5rem] h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{num}</span>
    <span className="text-slate-600 dark:text-slate-400 leading-relaxed">{children}</span>
  </li>
);

const MetricCard = ({ icon: Icon, label, desc }) => (
  <div className="flex gap-3 p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
      <Icon className="text-lg" />
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-800 dark:text-white">{label}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{desc}</p>
    </div>
  </div>
);

const TABS = ["Overview", "Process List", "Container"];

const TAB_DESCRIPTIONS = [
  { tab: "Overview", desc: "Displays key server health and performance metrics." },
  { tab: "Process List", desc: "Displays list of processes and service state." },
  { tab: "Container", desc: "Displays all the monitored containers." },
];

export function renderHost() {
  return <HostComponent />;
}

function HostComponent() {
  /* ─── main export ─── */
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null); // null = show all

  const handleTabClick = (tab) => {
    setActiveTab(tab === activeTab ? null : tab);
  };

  const showSection = (tab) => activeTab === null || activeTab === tab;

  const visibleDescriptions = activeTab
    ? TAB_DESCRIPTIONS.filter(t => t.tab === activeTab)
    : TAB_DESCRIPTIONS;

  return (
    <main className="transition-opacity duration-300 space-y-16">

      {/* Breadcrumbs */}
      <Breadcrumb items={[
        { label: "Netraa APM" },
        { label: "Analyze & Explore" },
        { label: "Apps" },
        { label: "Infrastructure" },
        { label: "Host" }
      ]} />

      {/* PAGE HEADING */}
      <div className="border-b border-gray-200 dark:border-gray-800 pb-4">
        <div className="flex flex-col lg:flex-row lg:items-start gap-5 mb-6">

          {/* LEFT SIDE */}
          <div className="flex-1 min-w-0 scroll-mt-24" id="intro">
            <h1 className="text-3xl lg:text-5xl font-extrabold text-text-main dark:text-white tracking-tight leading-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                Host
              </span>
              <span className="text-orange-500">:</span>{" "}
              Centralized Host Monitoring
            </h1>

            <p className="mt-3 text-lg lg:text-xl text-slate-500 dark:text-slate-400 leading-relaxed font-light">
              The Host page provides a centralized view of all monitored servers, acting as the primary entry point for operational visibility.
            </p>
          </div>

          {/* RIGHT SIDE BUTTON */}
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={() => {
                navigate("/agentsetup", { state: { scrollTo: "Infra" } });
              }}
              className="px-5 py-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400 rounded-xl font-semibold whitespace-nowrap hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors inline-flex items-center gap-2"
            >
              <FiChevronRight className="rotate-180 w-4 h-4" />
              Instrument a Host
            </button>
          </div>

        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <MetricCard icon={MdSpeed} label="Health & Availability" desc="Quickly evaluate the health, availability, and performance of servers across the environment." />
          <MetricCard icon={MdTimeline} label="Real-Time Metrics" desc="Access real-time metrics to identify potential issues before they escalate." />
          <MetricCard icon={MdNetworkCheck} label="Operational Overview" desc="Maintain a comprehensive overview of server operations, ensuring consistent reliability and performance." />
        </div>
      </div>

      {/* SECTION: Accessing the Host Page */}
      <section id="accessing-host" className="scroll-mt-24">
        <SectionHeading
          icon={<HiOutlineServer className="text-xl" />}
          label="Accessing the Host Page"
          id="accessing-host"
          color="primary/10 text-primary"
        />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
          To view monitored servers in Netraa, follow these steps:
        </p>
        <ol className="space-y-3">
          <StepItem num={1}>
            <strong className="text-slate-800 dark:text-slate-200">Log In —</strong> Sign in to the Netraa platform using your credentials.
          </StepItem>
          <StepItem num={2}>
            <strong className="text-slate-800 dark:text-slate-200">Default Landing Page —</strong> Upon successful authentication, you will be automatically redirected to the Services page, which serves as the default landing page.
          </StepItem>
          <StepItem num={3}>
            <strong className="text-slate-800 dark:text-slate-200">Navigate to Host —</strong> From the left-side menu bar, click on <strong className="text-slate-800 dark:text-slate-200">Infrastructure</strong>. Under Infrastructure, select <strong className="text-slate-800 dark:text-slate-200">Host</strong>.
          </StepItem>
        </ol>
        <img src={host1Img} alt="Host Page – Overview" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
      </section>

      {/* SECTION: Exploring a Host */}
      <section id="exploring-host" className="scroll-mt-24 space-y-6">
        <SectionHeading
          icon={<MdDeviceHub className="text-xl" />}
          label="Exploring a Host"
          id="exploring-host"
          color="primary/10 text-primary"
        />
        <ol className="space-y-3">
          <StepItem num={1}>
            From the Host page, click on the desired <strong className="text-slate-800 dark:text-slate-200">Host</strong> entry.
          </StepItem>
          <StepItem num={2}>
            This action opens the <strong className="text-slate-800 dark:text-slate-200">Host Details page</strong>.
          </StepItem>
        </ol>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          The Host Details page provides comprehensive insights into the selected Host, including:
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <MetricCard icon={MdSpeed} label="Health & Availability" desc="Health and availability metrics." />
          <MetricCard icon={MdTimeline} label="Resource Utilization" desc="Resource utilization trends." />
          <MetricCard icon={MdBugReport} label="Alerts & Issues" desc="Alerts and issues specific to the Host." />
        </div>

        {/* ── Interactive Tab Strip ── */}
        <div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
            The Host Details page is organized into the following tabs:
          </p>
          <div className="flex flex-wrap gap-2">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer
                  ${activeTab === tab
                    ? "bg-primary text-white border-primary shadow-sm shadow-primary/30"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:text-primary dark:hover:text-primary"
                  }`}
              >
                {tab}
              </button>
            ))}

            {/* Show All — only visible when a tab is active */}
            {activeTab !== null && (
              <button
                onClick={() => setActiveTab(null)}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-orange-400 text-orange-500 bg-orange-50 dark:bg-orange-950/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-all duration-200"
              >
                Show All
              </button>
            )}
          </div>
        </div>

        {/* ── Tab Description Table (filtered) ── */}
        <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {visibleDescriptions.map(({ tab, desc }) => (
            <div
              key={tab}
              className={`flex gap-4 px-5 py-3.5 transition-colors duration-200
                ${activeTab === tab
                  ? "bg-orange-50 dark:bg-orange-950/20"
                  : "bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                }`}
            >
              <span className="min-w-[110px] text-sm font-semibold text-primary">{tab}</span>
              <span className="text-sm text-slate-600 dark:text-slate-400">{desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION: Overview */}
      {showSection("Overview") && (
        <section id="overview" className="scroll-mt-24 space-y-6" style={{ animation: "fadeIn 0.3s ease" }}>
          <SectionHeading
            icon={<MdMonitor className="text-xl" />}
            label="Overview"
            id="overview"
            color="primary/10 text-primary"
          />
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            The Overview page provides a comprehensive summary of the selected server, presenting key health metrics in a single, consolidated view. It enables users to quickly access and evaluate:
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard icon={MdSpeed} label="CPU Utilization" desc="Current and historical CPU usage across the server." />
            <MetricCard icon={MdMemory} label="Memory Usage" desc="Real-time and trend data for memory consumption." />
            <MetricCard icon={MdStorage} label="Swap Memory" desc="Swap memory consumption metrics." />
            <MetricCard icon={MdTimeline} label="Average Server Load" desc="Average load across the server over time." />
            <MetricCard icon={MdNetworkCheck} label="Network Activity" desc="Inbound and outbound network traffic metrics." />
            <MetricCard icon={MdDns} label="Disk Usage" desc="Disk consumption and availability metrics." />
          </div>
          <img src={host3Img} alt="Host Overview" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
          <img src={host4Img} alt="Host Overview Metrics" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
          <img src={host5Img} alt="Host Overview Detail" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
            <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
              <strong>Alert Summary</strong> — There is an alert summary tile which shows the current active problems in the server.
            </p>
            <img src={host6Img} alt="Alert Summary" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
          </div>
        </section>
      )}

      {/* SECTION: Process List */}
      {showSection("Process List") && (
        <section id="process-list" className="scroll-mt-24 space-y-6" style={{ animation: "fadeIn 0.3s ease" }}>
          <SectionHeading
            icon={<MdList className="text-xl" />}
            label="Process List"
            id="process-list"
            color="primary/10 text-primary"
          />
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            The Process List page displays all processes currently running inside the selected Host. It provides a search bar that allows users to quickly locate a specific process by name.
          </p>
          <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {[
              { col: "COUNT", desc: "Gives the number of process instances in a specific process." },
              { col: "STATE", desc: "Shows the state of the process." },
              { col: "CPU%", desc: "Shows the CPU consumption percentage." },
              { col: "MEM%", desc: "Shows the Memory consumption percentage." },
              { col: "PID", desc: "Provides the PID of the process." },
              { col: "CHART", desc: "A clickable option which shows the graph trend for the memory and CPU consumption." },
              { col: "Service State", desc: "Shows the entire service state with color indicators and enabled filter feature for quickly accessing the service, plus a search bar for quick search." },
            ].map(({ col, desc }) => (
              <div key={col} className="flex gap-4 px-5 py-3.5 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <span className="min-w-[110px] text-sm font-semibold text-primary">{col}</span>
                <span className="text-sm text-slate-600 dark:text-slate-400">{desc}</span>
              </div>
            ))}
          </div>
          <img src={host7Img} alt="Process List" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
        </section>
      )}

      {/* SECTION: Container */}
      {showSection("Container") && (
        <section id="container" className="scroll-mt-24 space-y-6" style={{ animation: "fadeIn 0.3s ease" }}>
          <SectionHeading
            icon={<TbContainer className="text-xl" />}
            label="Container"
            id="container"
            color="primary/10 text-primary"
          />
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            The Container tab provides a list of all containers running on the selected server, along with color-coded indicators for quick status recognition.
          </p>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            For each container, the tab displays key performance metrics, including:
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <MetricCard icon={MdSpeed} label="CPU Usage (%)" desc="CPU consumption percentage for each container." />
            <MetricCard icon={MdMemory} label="Memory Usage (%)" desc="Memory consumption percentage for each container." />
            <MetricCard icon={MdNetworkCheck} label="Network Rate" desc="Inbound and outbound network rate for each container." />
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 border border-slate-200 dark:border-slate-700 space-y-3">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Container Attributes</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { label: "Name", desc: "The name assigned to the container." },
                { label: "ID", desc: "The unique identifier of the container." },
                { label: "Size", desc: "The storage size used by the container." },
                { label: "Created", desc: "The timestamp when the container was created." },
                { label: "Repo Tags", desc: "Repository tags associated with the container image." },
                { label: "CPU Limit", desc: "The maximum CPU allocation set for the container." },
                { label: "Memory Limit", desc: "The maximum memory allocation set for the container." },
              ].map(({ label, desc }) => (
                <div key={label} className="flex items-start gap-2 px-4 py-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5"></span>
                  <div>
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{label}: </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <img src={host8Img} alt="Container List" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
        </section>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

    </main>
  );
}